/**
 * Centralised toast helper. Wraps sonner so the rest of the app never imports
 * it directly — makes future swaps (or branded styling) painless.
 */
import { toast as sonnerToast, type ExternalToast } from 'sonner';

type ToastPayload = string | { title: string; description?: string };

function unwrap(payload: ToastPayload): {
  message: string;
  description?: string;
} {
  if (typeof payload === 'string') return { message: payload };
  return { message: payload.title, description: payload.description };
}

const defaultOpts: ExternalToast = {
  duration: 4500,
};

export interface UndoableToastOptions extends ExternalToast {
  /** Label for the undo action. Defaults to `Undo`. */
  actionLabel?: string;
  /** Fired when the user clicks "Undo" before the toast times out. */
  onUndo: () => void | Promise<void>;
  /** Optional description for multi-item toasts (e.g., "3 policies archived"). */
  description?: string;
}

export const toast = {
  success(payload: ToastPayload, opts?: ExternalToast) {
    const { message, description } = unwrap(payload);
    return sonnerToast.success(message, { ...defaultOpts, description, ...opts });
  },
  error(payload: ToastPayload, opts?: ExternalToast) {
    const { message, description } = unwrap(payload);
    return sonnerToast.error(message, {
      ...defaultOpts,
      duration: 6500,
      description,
      ...opts,
    });
  },
  info(payload: ToastPayload, opts?: ExternalToast) {
    const { message, description } = unwrap(payload);
    return sonnerToast(message, { ...defaultOpts, description, ...opts });
  },
  warning(payload: ToastPayload, opts?: ExternalToast) {
    const { message, description } = unwrap(payload);
    return sonnerToast.warning(message, { ...defaultOpts, description, ...opts });
  },
  loading(message: string, opts?: ExternalToast) {
    return sonnerToast.loading(message, { ...defaultOpts, ...opts });
  },
  /**
   * Success toast with an "Undo" action. Shows for 6s by default, which is
   * enough time to undo a destructive UI action (archive, hide, remove). The
   * `onUndo` handler is fired when clicked; errors are surfaced as an error toast.
   */
  undoable(message: string, { actionLabel = 'Undo', onUndo, ...opts }: UndoableToastOptions) {
    return sonnerToast.success(message, {
      duration: 6000,
      ...opts,
      action: {
        label: actionLabel,
        onClick: () => {
          try {
            const result = onUndo();
            if (result && typeof (result as Promise<unknown>).then === 'function') {
              (result as Promise<unknown>).catch(() =>
                sonnerToast.error('Undo failed')
              );
            }
          } catch {
            sonnerToast.error('Undo failed');
          }
        },
      },
    });
  },
  promise: sonnerToast.promise,
  dismiss: sonnerToast.dismiss,
};

export type { ExternalToast } from 'sonner';
